/**
 * PROPRIETARY AND CONFIDENTIAL
 * Copyright © 2026 Contractor Co-Pilot. All Rights Reserved.
 */

"use client";

import posthog from "posthog-js";
import { PostHogProvider as PHProvider, usePostHog } from "posthog-js/react";
import { usePathname, useSearchParams } from "next/navigation";
import { useEffect, useRef, useState, Suspense } from "react";
import { useCookieConsent } from "@/hooks/ui/use-cookie-consent";

const POSTHOG_KEY = process.env.NEXT_PUBLIC_POSTHOG_KEY;
const POSTHOG_HOST =
  process.env.NEXT_PUBLIC_POSTHOG_HOST ?? "https://us.i.posthog.com";

// Only enable in production (Vercel sets NODE_ENV=production on deploy)
const IS_ENABLED =
  typeof window !== "undefined" &&
  !!POSTHOG_KEY &&
  process.env.NODE_ENV === "production";

/**
 * Tracks SPA pageviews on route changes.
 * Wrapped in Suspense because useSearchParams() requires it in Next.js App Router.
 */
function PostHogPageView() {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const ph = usePostHog();
  const lastUrl = useRef("");

  useEffect(() => {
    if (!pathname || !ph) return;

    const url = searchParams?.size
      ? `${pathname}?${searchParams.toString()}`
      : pathname;

    // Avoid duplicate captures on same URL
    if (url === lastUrl.current) return;
    lastUrl.current = url;

    ph.capture("$pageview", { $current_url: window.origin + url });
  }, [pathname, searchParams, ph]);

  return null;
}

export function PostHogProvider({ children }: { children: React.ReactNode }) {
  const [initialized, setInitialized] = useState(false);
  const { isGranted } = useCookieConsent();

  useEffect(() => {
    if (!IS_ENABLED || initialized || !isGranted) return;

    posthog.init(POSTHOG_KEY!, {
      api_host: POSTHOG_HOST,
      person_profiles: "identified_only",
      capture_pageview: false, // We handle this manually for SPA route changes
      capture_pageleave: true,
      capture_exceptions: true, // Auto-capture JS exceptions for error tracking
      autocapture: {
        dom_event_allowlist: ["click", "submit"], // Selective autocapture for engagement
        element_allowlist: ["button", "a", "form"],
      },
      session_recording: {
        maskAllInputs: true,
        maskTextSelector: "[data-ph-mask]",
      },
    });

    setInitialized(true);
  }, [initialized, isGranted]);

  if (!IS_ENABLED || !isGranted) return <>{children}</>;

  return (
    <PHProvider client={posthog}>
      <Suspense fallback={null}>
        <PostHogPageView />
      </Suspense>
      {children}
    </PHProvider>
  );
}
